'use client';

import Link from '@/components/LocaleLink';
import { 
  Heart, Globe, Users, Headphones, 
  Shield, Zap, ChevronRight,
  Smartphone, Route, Languages
} from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageProvider';

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <div>
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('about.heroTitle')}</h1>
          <p className="text-xl text-white/90">{t('about.heroSubtitle')}</p>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('about.storyTitle')}</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('about.story1')}
              </p>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('about.story2')}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                {t('about.story3')}
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0"><Languages className="w-6 h-6 text-primary" /></div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.featureLanguages')}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{t('about.langDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0"><Heart className="w-6 h-6 text-secondary" /></div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('about.authentic')}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{t('about.authenticDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0"><Zap className="w-6 h-6 text-accent" /></div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('about.tech')}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{t('about.techDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('about.diffTitle')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Smartphone, title: t('home.createFromMobile'), desc: t('about.diff1') },
              { icon: Route, title: t('about.realGPS'), desc: t('about.diff2') },
              { icon: Globe, title: t('home.globalReach'), desc: t('about.diff3') },
              { icon: Shield, title: t('home.featureQuality'), desc: t('about.diff4') },
            ].map((item, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('about.valuesTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: t('about.community'), desc: t('about.communityDesc') },
              { icon: Shield, title: t('about.quality'), desc: t('about.qualityDesc') },
              { icon: Heart, title: t('about.equity'), desc: t('about.equityDesc') },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('about.teamTitle')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            {t('about.teamDesc')}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('blog.readyToExplore')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{t('about.ctaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/tours" className="btn-primary inline-flex items-center justify-center">{t('nav.exploreTours')} <ChevronRight className="ml-2 w-5 h-5" /></Link>
            <Link href="/guias" className="btn-outline inline-flex items-center justify-center"><Headphones className="mr-2 w-5 h-5" />{t('about.createTours')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
